import _ from 'lodash';
import type MarginType from '@this/domain/organization/margin_type2';
import localStorage from '@this/lib/local_storage';
import type ListWrapperInterface from '../list_wrapper_interface';
import Hotel from './hotel';
import type { HotelJson } from '../select_repository';
import type { HotelSearchEventTargetType } from './hotel_search_interaction_event';
import { filterHotelsByName, splitNameKeywords } from './hotel_name_filter';
import { recordHotelSearchInteractionEvent, sortKeyToEventTargetType } from './hotel_search_interaction_event';

interface Args {
  showFee?: boolean;
  includeTax?: boolean;
  package?: boolean;
  current?: string;
  marginType?: MarginType;
  isShowMap?: boolean;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface RangeInput {
  min: string;
  max: string;
}

interface HotelFilter {
  isShowFilter: boolean;
  maxPrice: number;
  minPrice: number;
  maxDistance: number;
  minDistance: number;
  priceInput: RangeInput;
  distanceInput: RangeInput;
  priceInputError: string | null;
  distanceInputError: string | null;
  onlyWithinLimit: boolean;
  nameKeyword: string;
  initialFilter: Omit<
    HotelFilter,
    | 'isShowFilter'
    | 'initialFilter'
    | 'onlyWithinLimit'
    | 'nameKeyword'
    | 'priceInput'
    | 'distanceInput'
    | 'priceInputError'
    | 'distanceInputError'
  >;
}

class HotelList implements ListWrapperInterface {
  includeTax: boolean;

  package: boolean;

  list: Hotel[];

  filteredList: Hotel[];

  sortKey: 'orderedIndex' | 'average_price_with_tax' | 'distance' | 'station_distance' | 'map';

  beforeSortKey: 'orderedIndex' | 'average_price_with_tax' | 'distance' | 'station_distance' | 'map';

  currentId: string | null | undefined;

  showMap: boolean;

  showBox: boolean;

  showFee: boolean;

  private marginType: MarginType | undefined;

  newId: string | null;

  segment: undefined; // only for typing

  filter: HotelFilter;

  hotelPriceLimit: number | null = null;

  constructor(rawHotels?: HotelJson[], args: Args = {}) {
    this.includeTax = !!args.includeTax;
    this.package = !!args.package;
    this.showFee = args.showFee || false;
    this.marginType = args.marginType;
    this.list =
      _.map(
        rawHotels,
        h =>
          new Hotel(
            _.merge(h, {
              includeTax: this.includeTax,
              package: this.package,
              showFee: this.showFee,
              marginType: this.marginType
            }),
            h.score_index
          )
      ) || [];
    this.filteredList = this.list;
    this.sortKey = 'orderedIndex';
    this.beforeSortKey = 'orderedIndex';
    this.filter = this.setFilter();

    const c: Hotel | null | undefined = this.currentHotelConstructor(args);
    if (c) {
      this.currentId = c.id;
    } else {
      const h = this.package ? _.minBy(this.list, hotel => hotel.price) : _.first(this.list);
      this.currentId = h && h.id;
    }

    if (args.isShowMap === true) {
      this.showMap = true;
    } else {
      this.showMap = false;
    }

    // for CSSTransition
    this.showBox = true;
    this.newId = null;

    this.select(localStorage.getItem('hotelId') ?? '');
  }

  // 選択されているホテルを構築する。
  currentHotelConstructor(args: Args = {}): Hotel | null | undefined {
    let current;
    if (args.current) {
      current = this.list.find(h => !!h.name && h.name.replace(/\n/g, '') + h.price === (args && args.current));
    }
    if (!current) {
      current = this.list.find(current => !!current.optimal);
    }
    return current;
  }

  setIncludeTax(value: boolean) {
    this.includeTax = value;
    this.list.forEach(h => {
      h.setIncludeTax(value);
    });
    this.filter = this.setFilter();
    this.search();
  }

  find(id: string | null | undefined): Hotel | undefined {
    if (id) return _.find(this.list, h => h.id === id);
    return undefined;
  }

  findByIndex(index: number) {
    return _.find(this.list, h => h.orderedIndex === index);
  }

  current(): Hotel {
    return this.find(this.currentId) || new Hotel();
  }

  first(): Hotel {
    return _.first(this.list) || new Hotel();
  }

  currentIndex() {
    const orderedIndex = this.current().orderedIndex;
    return orderedIndex != null ? orderedIndex + 1 : undefined;
  }

  isFirst(): boolean {
    return this.current().orderedIndex === 0;
  }

  select(id: string) {
    this.list.forEach(h => {
      h.hovered = false;
    });

    if (this.find(id)) {
      this.currentId = id;

      app.render();
    }
  }

  selectWithAnimation(id: string) {
    this.newId = id;
    app.render(); // スマホのために一度renderしてoutlineを表示しておく

    setTimeout(() => {
      this.showBox = false;
      app.render();
    }, 100); // スマホでCSSTransitionのonExitを発火させるため
  }

  handleTransitionExited() {
    this.showBox = true;
    if (this.newId) this.select(this.newId);
    this.newId = null;
  }

  selectByIndex(index: number) {
    const h = this.findByIndex(index);
    if (h) this.currentId = h.id;
    app.render();
  }

  fetchDistances(location: LatLng) {
    this.list.forEach(h => {
      if (h.latitude && h.longitude) {
        h.walkminute = utils.walkMinutesText(
          utils.calcDistance(location.lat, location.lng, h.latitude, h.longitude)
        );
      }
    });
  }

  sortBy(key: string) {
    const availableHotels = _.filter(this.list, h => !(h.sold_out && h.too_late));
    const unavailableHotels = _.filter(this.list, h => !!(h.sold_out && h.too_late));
    this.list = _.concat(_.sortBy(availableHotels, [key, 'orderedIndex']), unavailableHotels);
    this.search();
  }

  setSortBy(key: 'orderedIndex' | 'average_price_with_tax' | 'distance' | 'station_distance') {
    this.sortKey = key;
    const availableHotels = _.filter(this.list, h => !(h.sold_out && h.too_late));
    const unavailableHotels = _.filter(this.list, h => !!(h.sold_out && h.too_late));
    this.list = _.concat(_.sortBy(availableHotels, [key, 'orderedIndex']), unavailableHotels);
  }

  handleSortKeyChange =
    (key: 'orderedIndex' | 'average_price_with_tax' | 'distance' | 'station_distance') => () => {
      if (key === 'average_price_with_tax' && !this.includeTax) {
        this.sortBy('average_price');
      } else {
        this.sortBy(key);
      }
      this.sortKey = key;
      localStorage.setItem('hotelSortKey', key);
      this.showMap = false;
      this.recordTabSelect(sortKeyToEventTargetType(key));
      app.render();
    };

  handleToggleMap = () => {
    this.showMap = !this.showMap;
    if (this.showMap) {
      this.beforeSortKey = this.sortKey;
      this.sortKey = 'map';
      this.recordTabSelect('map');
    } else {
      this.sortKey = this.beforeSortKey;
    }
    app.render();
  };

  // 1回の検索を束ねるID（全ホテルで共通の search_query_item_id）
  get searchQueryItemId(): number | null | undefined {
    return this.list[0]?.search_query_item_id;
  }

  private recordTabSelect(eventTargetType: HotelSearchEventTargetType) {
    const { searchQueryItemId } = this;
    if (searchQueryItemId == null) return;
    recordHotelSearchInteractionEvent({
      searchQueryItemId,
      eventType: 'tab_select',
      eventTargetType
    });
  }

  // タブ内（または地図上）で宿が選択されたことを記録する。
  // display_index は表示中リスト（filteredList）内の順位。地図表示時は順位の概念がないため null。
  recordItemSelect(hotel: Hotel) {
    const searchQueryItemId = hotel.search_query_item_id ?? this.searchQueryItemId;
    if (searchQueryItemId == null || hotel.id == null) return;
    const isMap = this.sortKey === 'map';
    const foundIndex = this.filteredList.findIndex(h => h.id === hotel.id);
    recordHotelSearchInteractionEvent({
      searchQueryItemId,
      eventType: 'item_select',
      eventTargetType: sortKeyToEventTargetType(this.sortKey),
      displayIndex: isMap || foundIndex < 0 ? null : foundIndex,
      searchResultId: hotel.id
    });
  }

  length() {
    return this.list.length;
  }

  get filterLength() {
    return this.filteredList.length;
  }

  get allLength() {
    return this.list.length;
  }

  get hasNoDirectAir() {
    return false;
  }

  isCurrentHotelTooLate(): boolean {
    return !!this.current().too_late;
  }

  canReserveJustBefore(): boolean {
    return this.current().type === 'eps_rapid' || this.current().type === 'e';
  }

  updateHotelList(hotel: Hotel) {
    const targetHotel = this.find(hotel.id);
    if (targetHotel) {
      this.list = _.map(this.list, h => {
        if (h.id !== hotel.id) return h;

        if (!hotel.orderedIndex) hotel.orderedIndex = h.orderedIndex;
        return hotel;
      });
    }
    this.updateFilteredList(hotel);
    app.render();
  }

  updateFilteredList(hotel: Hotel) {
    const targetHotel = this.filteredList.find(h => h.id === hotel.id);
    if (targetHotel) {
      this.filteredList = _.map(this.filteredList, h => {
        if (h.id !== hotel.id) return h;

        if (hotel.orderedIndex == null) hotel.orderedIndex = h.orderedIndex;
        return hotel;
      });
    }
    app.render();
  }

  search() {
    const { minPrice, maxPrice, minDistance, maxDistance, onlyWithinLimit, initialFilter } = this.filter;
    const limit = this.hotelPriceLimit;
    const isFiltering =
      onlyWithinLimit ||
      minPrice !== initialFilter.minPrice ||
      maxPrice !== initialFilter.maxPrice ||
      minDistance !== initialFilter.minDistance ||
      maxDistance !== initialFilter.maxDistance;
    const priceFilteredList = _.filter(this.list, h => {
      if (!isFiltering) return true;
      const price = this.priceForFilter(h);
      if (price == null || !h.distance) return false;
      if (price < minPrice || price > maxPrice) return false;
      if (h.distance < minDistance || h.distance > maxDistance) return false;
      if (onlyWithinLimit && limit && h.average_price_with_tax && h.average_price_with_tax > limit) return false;
      return true;
    });
    this.filteredList = filterHotelsByName(priceFilteredList, this.filter.nameKeyword);
    app.render();
  }

  setFilter(): HotelFilter {
    const prices = this.list.map(h => this.priceForFilter(h)).filter((price): price is number => price != null);
    const initialFilter = {
      maxPrice: _.max(prices) || 0,
      minPrice: _.min(prices) || 0,
      maxDistance: _.maxBy(this.list, h => h.distance)?.distance || 0,
      minDistance: _.minBy(this.list, h => h.distance)?.distance || 0
    };

    return {
      isShowFilter: false,
      onlyWithinLimit: false,
      nameKeyword: '',
      ...initialFilter,
      priceInput: {
        min: `${initialFilter.minPrice}`,
        max: `${initialFilter.maxPrice}`
      },
      distanceInput: {
        min: (initialFilter.minDistance / 1000).toFixed(1),
        max: (initialFilter.maxDistance / 1000).toFixed(1)
      },
      priceInputError: null,
      distanceInputError: null,
      initialFilter
    };
  }

  private priceForFilter(hotel: Hotel): number | null {
    if (this.includeTax) {
      return hotel.average_price_with_tax ?? null;
    }

    return hotel.average_price ?? null;
  }

  setHotelPriceLimit(limit: number | null) {
    this.hotelPriceLimit = limit;
  }

  setPriceInput(key: keyof RangeInput, value: string) {
    const priceInput = { ...this.filter.priceInput, [key]: value };
    const minPrice = this.parsePriceInput(priceInput.min);
    const maxPrice = this.parsePriceInput(priceInput.max);
    const hasInvalidPriceInput =
      !this.isValidPriceInput(priceInput.min) || !this.isValidPriceInput(priceInput.max);

    const error = hasInvalidPriceInput ? '数値を入力してください' : this.rangeInputError(minPrice, maxPrice);

    this.filter = { ...this.filter, priceInput, priceInputError: error };
    if (error) {
      app.render();
      return;
    }

    this.filter = {
      ...this.filter,
      minPrice: minPrice ?? this.filter.initialFilter.minPrice,
      maxPrice: maxPrice ?? this.filter.initialFilter.maxPrice,
      onlyWithinLimit: false
    };

    this.search();
  }

  normalizePriceInput() {
    const { min, max } = this.filter.priceInput;
    const minPrice = this.parsePriceInput(min);
    const maxPrice = this.parsePriceInput(max);

    if (!this.isValidPriceInput(min) || !this.isValidPriceInput(max) || this.rangeInputError(minPrice, maxPrice)) {
      return;
    }

    const initial = this.filter.initialFilter;
    const adjustedMinPrice =
      minPrice === null ? initial.minPrice : this.clamp(minPrice, initial.minPrice, initial.maxPrice);
    const adjustedMaxPrice =
      maxPrice === null ? initial.maxPrice : this.clamp(maxPrice, initial.minPrice, initial.maxPrice);

    this.filter = {
      ...this.filter,
      minPrice: adjustedMinPrice,
      maxPrice: adjustedMaxPrice,
      priceInput: {
        min: minPrice === null ? '' : `${adjustedMinPrice}`,
        max: maxPrice === null ? '' : `${adjustedMaxPrice}`
      },
      priceInputError: null
    };
    this.search();
  }

  setDistanceInput(key: keyof RangeInput, value: string) {
    const distanceInput = { ...this.filter.distanceInput, [key]: value };
    const minDistance = key === 'min' ? this.parseDistanceInput(distanceInput.min) : this.filter.minDistance;
    const maxDistance = key === 'max' ? this.parseDistanceInput(distanceInput.max) : this.filter.maxDistance;
    const hasInvalidDistanceInput =
      !this.isValidDistanceInput(distanceInput.min) || !this.isValidDistanceInput(distanceInput.max);

    const error = hasInvalidDistanceInput
      ? '数値を入力してください'
      : this.rangeInputError(minDistance, maxDistance);

    this.filter = { ...this.filter, distanceInput, distanceInputError: error };
    if (error) {
      app.render();
      return;
    }

    this.filter = {
      ...this.filter,
      minDistance: minDistance ?? this.filter.initialFilter.minDistance,
      maxDistance: maxDistance ?? this.filter.initialFilter.maxDistance
    };
    this.search();
  }

  normalizeDistanceInput(key: keyof RangeInput) {
    const { min, max } = this.filter.distanceInput;
    const minDistance = key === 'min' ? this.parseDistanceInput(min) : this.filter.minDistance;
    const maxDistance = key === 'max' ? this.parseDistanceInput(max) : this.filter.maxDistance;

    if (
      !this.isValidDistanceInput(min) ||
      !this.isValidDistanceInput(max) ||
      this.rangeInputError(minDistance, maxDistance)
    ) {
      return;
    }

    const initial = this.filter.initialFilter;
    const adjustedMinDistance =
      minDistance === null
        ? initial.minDistance
        : this.clamp(minDistance, initial.minDistance, initial.maxDistance);
    const adjustedMaxDistance =
      maxDistance === null
        ? initial.maxDistance
        : this.clamp(maxDistance, initial.minDistance, initial.maxDistance);

    this.filter = {
      ...this.filter,
      minDistance: adjustedMinDistance,
      maxDistance: adjustedMaxDistance,
      distanceInput: {
        min: minDistance === null ? '' : (adjustedMinDistance / 1000).toFixed(1),
        max: maxDistance === null ? '' : (adjustedMaxDistance / 1000).toFixed(1)
      },
      distanceInputError: null
    };
    this.search();
  }

  private isValidPriceInput(value: string): boolean {
    // 価格は0以上の整数のみ許可する
    return value === '' || /^\d+$/.test(value);
  }

  private isValidDistanceInput(value: string): boolean {
    // 距離は0以上で小数第1位まで許可する
    return value === '' || /^\d+(\.\d)?$/.test(value);
  }

  private parsePriceInput(value: string): number | null {
    if (value === '') return null;
    return Number(value);
  }

  private parseDistanceInput(value: string): number | null {
    if (value === '') return null;

    return Math.round(Number(value) * 1000);
  }

  private rangeInputError(min: number | null, max: number | null): string | null {
    if (min !== null && max !== null && min > max) return '下限値は上限値以下で入力してください';
    return null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  toggleShowFilter() {
    this.filter = { ...this.filter, isShowFilter: !this.filter.isShowFilter };
    app.render();
  }

  toggleOnlyWithinLimit() {
    const next = !this.filter.onlyWithinLimit;
    if (next && this.hotelPriceLimit != null && this.hotelPriceLimit > 0) {
      this.filter = {
        ...this.filter,
        onlyWithinLimit: true,
        maxPrice: this.hotelPriceLimit
      };
    } else {
      this.filter = {
        ...this.filter,
        onlyWithinLimit: false,
        maxPrice: this.filter.initialFilter.maxPrice
      };
    }
    this.search();
  }

  clearFilter() {
    this.filter = {
      ...this.filter,
      ...this.filter.initialFilter,
      priceInput: {
        min: `${this.filter.initialFilter.minPrice}`,
        max: `${this.filter.initialFilter.maxPrice}`
      },
      distanceInput: {
        min: (this.filter.initialFilter.minDistance / 1000).toFixed(1),
        max: (this.filter.initialFilter.maxDistance / 1000).toFixed(1)
      },
      priceInputError: null,
      distanceInputError: null,
      onlyWithinLimit: false,
      nameKeyword: ''
    };
    this.search();
  }

  setNameKeyword(keyword: string) {
    this.filter = { ...this.filter, nameKeyword: keyword };
    this.search();
  }

  suggestByName(keyword: string, limit = 20): Hotel[] {
    if (splitNameKeywords(keyword).length === 0) return [];

    const matched = filterHotelsByName(this.list, keyword);
    return _.uniqBy(matched, h => (h.name ?? '').toLowerCase()).slice(0, limit);
  }

  // Sliderの初期値とラベルを設定
  // 両端を切り上げ切り捨てして、初期値が両端の場合は初期値を表示する（各ステップの値がpriceStepの倍数になるように設定）
  priceSliderMarks(priceStep: number) {
    const minPrice = Math.floor(this.filter.initialFilter.minPrice / priceStep) * priceStep;
    const maxPrice = Math.ceil(this.filter.initialFilter.maxPrice / priceStep) * priceStep;
    return [
      { value: minPrice, label: utils.formatPrice(this.filter.initialFilter.minPrice) },
      { value: maxPrice, label: utils.formatPrice(this.filter.initialFilter.maxPrice) }
    ];
  }

  minPriceValue(currentMinPrice: number, marks: { value: number; label: string }[]) {
    const value = currentMinPrice === this.filter.initialFilter.minPrice ? marks[0].value : currentMinPrice;
    return Math.min(Math.max(value, marks[0].value), marks[1].value);
  }

  maxPriceValue(currentMaxPrice: number, marks: { value: number; label: string }[]) {
    const value = currentMaxPrice === this.filter.initialFilter.maxPrice ? marks[1].value : currentMaxPrice;
    return Math.min(Math.max(value, marks[0].value), marks[1].value);
  }

  // Sliderの値が変更されたときの処理
  // 両端の丸めた値の場合は元の最大値・最小値に戻す
  handlePriceChange = (newValue: number | number[], marks: { value: number; label: string }[]) => {
    const values = newValue as number[];
    const min = values[0] === marks[0].value ? this.filter.initialFilter.minPrice : values[0];
    const max = values[1] === marks[1].value ? this.filter.initialFilter.maxPrice : values[1];
    this.filter = {
      ...this.filter,
      minPrice: min,
      maxPrice: max,
      priceInput: { min: `${min}`, max: `${max}` },
      priceInputError: null,
      onlyWithinLimit: false
    };
    this.search();
  };

  // Sliderのラベルをフォーマット
  // 両端の丸めた値の場合は元の最大値・最小値を表示する
  priceValueLabelFormat = (value: number, marks: { value: number; label: string }[]) => {
    const price =
      value === marks[1].value
        ? this.filter.initialFilter.maxPrice
        : value === marks[0].value
        ? this.filter.initialFilter.minPrice
        : value;
    return utils.formatPrice(price);
  };

  // Sliderの初期値を設定
  // 両端を切り上げ切り捨てして、初期値が両端の場合は初期値を表示する（各ステップの値がdistanceStepの倍数になるように設定）
  distanceSliderMarks(distanceStep: number) {
    const minDistance = Math.floor(this.filter.initialFilter.minDistance / distanceStep) * distanceStep;
    const maxDistance = Math.ceil(this.filter.initialFilter.maxDistance / distanceStep) * distanceStep;
    return [
      { value: minDistance, label: `${(this.filter.initialFilter.minDistance / 1000).toFixed(1)}km` },
      { value: maxDistance, label: `${(this.filter.initialFilter.maxDistance / 1000).toFixed(1)}km` }
    ];
  }

  minDistanceValue(currentMinDistance: number, marks: { value: number; label: string }[]) {
    const value =
      currentMinDistance === this.filter.initialFilter.minDistance ? marks[0].value : currentMinDistance;
    return Math.min(Math.max(value, marks[0].value), marks[1].value);
  }

  maxDistanceValue(currentMaxDistance: number, marks: { value: number; label: string }[]) {
    const value =
      currentMaxDistance === this.filter.initialFilter.maxDistance ? marks[1].value : currentMaxDistance;
    return Math.min(Math.max(value, marks[0].value), marks[1].value);
  }

  // Sliderの値が変更されたときの処理
  // 両端の丸めた値の場合は元の最大値・最小値に戻す
  handleDistanceChange = (newValue: number | number[], marks: { value: number; label: string }[]) => {
    const values = newValue as number[];
    const min = values[0] === marks[0].value ? this.filter.initialFilter.minDistance : values[0];
    const max = values[1] === marks[1].value ? this.filter.initialFilter.maxDistance : values[1];
    this.filter = {
      ...this.filter,
      minDistance: min,
      maxDistance: max,
      distanceInput: { min: (min / 1000).toFixed(1), max: (max / 1000).toFixed(1) },
      distanceInputError: null
    };
    this.search();
  };

  // Sliderのラベルをフォーマット
  // 両端の丸めた値の場合は元の最大値・最小値を表示する
  distanceValueLabelFormat = (value: number, marks: { value: number; label: string }[]) => {
    const distance =
      value === marks[1].value
        ? this.filter.initialFilter.maxDistance
        : value === marks[0].value
        ? this.filter.initialFilter.minDistance
        : value;
    return `${(distance / 1000).toFixed(1)}km`;
  };

  get priceRange(): string {
    return `${utils.formatPrice(this.filter.minPrice)} ~ ${utils.formatPrice(this.filter.maxPrice)}`;
  }

  get distanceRange(): string | null {
    const maxDistance = (this.filter.maxDistance / 1000).toFixed(1);
    const minDistance = (this.filter.minDistance / 1000).toFixed(1);

    return `${minDistance}km ~ ${maxDistance}km`;
  }
}

export default HotelList;
